import { observer } from 'mobx-react-lite'
import React, { useState, useEffect } from 'react'
import {
  MDBBtn,
  MDBIcon,
  MDBModal,
  MDBModalBody,
  MDBModalHeader,
  MDBInput,
  MDBRow,
  MDBCol,
  MDBModalDialog,
  MDBModalContent,
  MDBModalTitle,
} from 'mdb-react-ui-kit'
import MDB4Datatable from '../../../../components/content/MDB4Datatable/MDB4Datatable.component'
import CarrierMgmtStore from './../../CarrierMgmt.store'
import CarrierContactService from './../../../../shared/services/CarrierContact.service'
import { UIInputToggleSwitch } from './../../../../components/forms/form-fields'
import { phoneToStorage, phoneToView } from './../../../../shared/formatters/telephone.formatter'

import './CarrierTable.scss'

const CARRIER_COLUMNS = [
  {
    label: 'Carriers',
    field: 'name',
    sort: 'asc',
  },
  {
    label: 'Description',
    field: 'description',
  },
  {
    label: '',
    field: 'edit',
  },
]

const CONTACT_COLUMNS = [
  {
    label: 'Contact Name',
    field: 'name',
    sort: 'asc',
  },
  {
    label: 'Carrier',
    field: 'carrier',
  },
  {
    label: 'Email',
    field: 'email',
  },
  {
    label: 'Phone',
    field: 'phone',
  },
  {
    label: '',
    field: 'edit',
  },
]

const CarrierTable = ({ goTo, filters }) => {
  const [showContacts, setShowContacts] = useState(false)
  const [contacts, setContacts] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [editingContact, setEditingContact] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [formChanges, setFormChanges] = useState({})
  const [isSaving, setIsSaving] = useState(false)

  const currentView = CarrierMgmtStore.getView()

  // Fetch contacts when toggle is switched or view changes
  useEffect(() => {
    const fetchContactsData = async () => {
      if (showContacts) {
        setIsLoading(true)

        try {
          const carriers = CarrierMgmtStore.getCarriers(true) || []
          const currentCarriers = carriers.filter(Carrier =>
            currentView === 'active' ? !!Carrier.get('c_active') : !Carrier.get('c_active')
          )

          const carrierIds = currentCarriers.map(c => c.id())

          if (carrierIds.length > 0) {
            const result = await CarrierContactService.search({
              search: { carrier_id: carrierIds },
              pagination: false,
            })
            setContacts(result?.models || result || [])
          } else {
            setContacts([])
          }
        } catch (error) {
          console.error('Failed to fetch contacts:', error)
          setContacts([])
        }

        setIsLoading(false)
      }
    }

    fetchContactsData()
  }, [showContacts, currentView])

  filters = filters && typeof filters === 'object' ? filters : {}
  const showSpec = !!filters?.spec,
    limitPriority = showSpec && !!filters?.priority,
    spec = showSpec && filters.spec,
    priority = limitPriority && filters?.priority,
    columns = (showSpec ? [{ label: '', field: 'priority' }].concat(CARRIER_COLUMNS) : CARRIER_COLUMNS).map(col =>
      col.field === 'name'
        ? {
            ...col,
            label: currentView === 'active' ? 'Active Carriers' : 'Inactive Carriers',
          }
        : col
    )

  const renderEditBtn = carrierId => (
    <MDBBtn type='button' color='default' onClick={() => goTo(carrierId)}>
      <MDBIcon icon={'edit'} />
    </MDBBtn>
  )

  const buildRow = Carrier => {
    let carObj = {
      name: Carrier.get('c_name'),
      description: `${Carrier.get('c_description') || ''}`,
      edit: renderEditBtn(Carrier.id()),
    }

    if (showSpec) {
      if (!Carrier.isSpeciality(spec)) return false

      if (limitPriority) {
        if (['core', 'preferred', 'additional'].includes(priority)) {
          if (!Carrier.isPriority(spec, 92, priority)) return false
        } else {
          return false
        }
      }

      carObj.priority = Carrier.renderPriorityIndicator(92)
    }

    return carObj
  }

  const renderTable = carriers => {
    const rows = carriers.map(buildRow).filter(c => c)
    return (
      <MDB4Datatable
        data={{ columns, rows }}
        sortRows={['name']}
        order={['name', 'asc']}
        bordered
        small
        displayEntries={false}
        entries={10}
        className={'text-capitalize carrier-list-table'}
      />
    )
  }

  const renderActiveTable = () =>
    renderTable((CarrierMgmtStore.getCarriers(true) || []).filter(Carrier => !!Carrier.get('c_active')))

  const renderInctiveTable = () =>
    renderTable((CarrierMgmtStore.getCarriers(true) || []).filter(Carrier => !Carrier.get('c_active')))

  const buildContactRow = contact => {
    // Find the carrier name
    const carrier = (CarrierMgmtStore.getCarriers(true) || []).find(c => c.id() === contact.carrier_id)
    const carrierName = carrier ? carrier.get('c_name') : 'Unknown'

    // Format phone number
    const formattedPhone = contact.phone ? phoneToView(contact.phone) : ''
    const phoneDisplay = formattedPhone
      ? `${formattedPhone}${contact.phone_ext ? ` ext. ${contact.phone_ext}` : ''}`
      : ''

    return {
      name: contact.name || '',
      carrier: carrierName,
      email: contact.email || '',
      phone: phoneDisplay,
      edit: (
        <div style={{ textAlign: 'center' }}>
          <MDBBtn type='button' color='default' onClick={() => handleEditContact(contact)}>
            <MDBIcon icon='edit' />
          </MDBBtn>
        </div>
      ),
    }
  }

  const handleEditContact = contact => {
    setEditingContact(contact)
    setFormChanges({})
    setModalOpen(true)
  }

  const handleCloseModal = () => {
    setModalOpen(false)
    setEditingContact(null)
    setFormChanges({})
  }

  const getFormValue = field => {
    if (formChanges.hasOwnProperty(field)) {
      return formChanges[field] || ''
    }
    if (editingContact && editingContact.hasOwnProperty(field)) {
      let val = editingContact[field] || ''
      if (field === 'phone' && val) {
        return phoneToView(val)
      }
      return val
    }
    return ''
  }

  const handleFormChange = e => {
    setFormChanges({
      ...formChanges,
      [e.target.name]: e.target.value,
    })
  }

  const handleSaveContact = async e => {
    e.preventDefault()
    setIsSaving(true)

    try {
      const updatedContact = {
        ...editingContact,
        ...formChanges,
        phone: phoneToStorage(getFormValue('phone')),
      }

      const result = await CarrierContactService.update(editingContact.id, updatedContact)

      if (result) {
        // Refresh contacts list
        const carriersList = CarrierMgmtStore.getCarriers(true) || []
        const currentCarriers = carriersList.filter(Carrier =>
          currentView === 'active' ? !!Carrier.get('c_active') : !Carrier.get('c_active')
        )
        const carrierIds = currentCarriers.map(c => c.id())

        if (carrierIds.length > 0) {
          const contactsResult = await CarrierContactService.search({
            search: { carrier_id: carrierIds },
            pagination: false,
          })
          setContacts(contactsResult?.models || contactsResult || [])
        }

        handleCloseModal()
      }
    } catch (error) {
      console.error('Failed to save contact:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const renderContactsTable = () => {
    const rows = contacts.map(buildContactRow)
    return (
      <MDB4Datatable
        data={{
          columns: CONTACT_COLUMNS,
          rows,
        }}
        sortRows={['name']}
        order={['name', 'asc']}
        bordered
        small
        displayEntries={false}
        entries={10}
        className={'text-capitalize contact-list-table'}
      />
    )
  }

  return (
    <div
      className={`CarrierTableComponent  ${showSpec ? 'show-priority' : ''} ${
        showContacts ? 'contacts-list' : 'carriers-list'
      }`}
    >
      <div className='view-selector'>
        <ul>
          <li className={currentView === 'active' ? 'active' : ''} onClick={() => CarrierMgmtStore.setView('active')}>
            Active Carriers
          </li>
          <li
            className={currentView === 'inactive' ? 'active' : ''}
            onClick={() => CarrierMgmtStore.setView('inactive')}
          >
            Inactive Carriers
          </li>
        </ul>
      </div>

      <div className='view-controls'>
        <span className='toggle-label'>View Mode: Showing {showContacts ? 'Contacts' : 'Carriers'}</span>
        <div className='toggle-container-wrapper'>
          <UIInputToggleSwitch
            id='carrier-contacts-toggle'
            name='showContacts'
            value={showContacts}
            onChange={e => setShowContacts(!!e.target.value)}
            labelOn='Switch to Carriers'
            labelOff='Switch to Contacts'
          />
        </div>
        <div
          className={
            'loader-wheel ' + (CarrierMgmtStore.getIsLoading('fetch.carriers') || isLoading ? 'is-loading' : '')
          }
        >
          <span>
            <i className='fa fa-spin fa-spinner' />
          </span>
          &nbsp;
          <strong>Updating {isLoading ? 'Contacts' : 'Carriers'} ...</strong>
        </div>
      </div>

      {!showContacts && currentView === 'active' && renderActiveTable()}
      {!showContacts && currentView === 'inactive' && renderInctiveTable()}
      {showContacts && renderContactsTable()}

      {/* Edit Contact Modal */}
      <MDBModal open={modalOpen} onClose={handleCloseModal}>
        <MDBModalDialog size='lg'>
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>
                Edit Contact
                <MDBBtn type='button' className='btn-close' color='none' onClick={handleCloseModal}></MDBBtn>
              </MDBModalTitle>
            </MDBModalHeader>
            <MDBModalBody>
              <form noValidate onSubmit={handleSaveContact}>
                <MDBRow>
                  <MDBCol size='12'>
                    <MDBInput
                      label='Contact Name'
                      type='text'
                      name='name'
                      value={getFormValue('name')}
                      onChange={handleFormChange}
                      autoComplete='off'
                    />
                  </MDBCol>
                  <MDBCol size='12' sm='6'>
                    <MDBInput
                      label='Title or Position'
                      type='text'
                      name='title'
                      value={getFormValue('title')}
                      onChange={handleFormChange}
                      autoComplete='off'
                    />
                  </MDBCol>
                  <MDBCol size='12' sm='6'>
                    <MDBInput
                      label='Department'
                      type='text'
                      name='department'
                      value={getFormValue('department')}
                      onChange={handleFormChange}
                      autoComplete='off'
                    />
                  </MDBCol>
                  <MDBCol size='12'>
                    <MDBInput
                      label='Email'
                      type='text'
                      name='email'
                      value={getFormValue('email')}
                      onChange={handleFormChange}
                      autoComplete='off'
                    />
                  </MDBCol>
                  <MDBCol size='9'>
                    <MDBInput
                      label='Phone'
                      type='text'
                      name='phone'
                      value={getFormValue('phone')}
                      onChange={handleFormChange}
                      autoComplete='off'
                    />
                  </MDBCol>
                  <MDBCol size='3'>
                    <MDBInput
                      label='Extension'
                      type='text'
                      name='phone_ext'
                      value={getFormValue('phone_ext')}
                      onChange={handleFormChange}
                      autoComplete='off'
                    />
                  </MDBCol>
                  <MDBCol size='12'>
                    <MDBInput
                      label='Notes'
                      type='textarea'
                      name='notes'
                      value={getFormValue('notes')}
                      onChange={handleFormChange}
                    />
                  </MDBCol>
                </MDBRow>
                <MDBRow>
                  <MDBCol size='12' sm='7'>
                    <MDBBtn
                      color='default'
                      className='btn-block'
                      type='submit'
                      disabled={!getFormValue('name') || isSaving}
                    >
                      {isSaving ? (
                        <span>
                          Saving...&nbsp;
                          <i className='fa fa-spin fa-spinner' />
                        </span>
                      ) : (
                        'Save Contact'
                      )}
                    </MDBBtn>
                  </MDBCol>
                  <MDBCol size='12' sm='5'>
                    <MDBBtn
                      type='button'
                      color='default'
                      className='btn-block'
                      onClick={handleCloseModal}
                      disabled={isSaving}
                    >
                      Cancel
                    </MDBBtn>
                  </MDBCol>
                </MDBRow>
              </form>
            </MDBModalBody>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>
    </div>
  )
}

export default observer(CarrierTable)
